import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  Calendar,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  DollarSign,
  Download,
  Eye,
  FileDown,
  FilePlus,
  FileSpreadsheet,
  GripVertical,
  Loader2,
  Pencil,
  RefreshCw,
  Save,
  Search,
  Trash2,
  X,
  ClipboardList,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  companySettingsApi,
  payrollBuilderReportTemplatesApi,
  payrollRunApi,
  type PayrollBuilderReportTemplateDto,
} from '../services/api';
import {
  getEnabledPayrollCycleOptions,
  type PayrollCycleOption,
} from '../utils/payrollCycles';
import type { Employee, PayrollRun, PayrollRunPreviewDto, PaymentType, PayRecord } from '../types';
import { useWeekStartDay } from '../hooks/useWeekStartDay';
import { usePermissions } from '../hooks/usePermissions';
import { WeekPickerCalendar } from '../components/payroll/WeekPickerCalendar';
import EmployeeAdvancedFilterPanel from '../components/EmployeeAdvancedFilterPanel';
import NamePromptModal from '../components/NamePromptModal';
import MessageDialog, { type MessageDialogOpen } from '../components/MessageDialog';
import ReportRunStatusBadge from '../components/ReportRunStatusBadge';
import {
  FilterCondition,
  FilterFieldOption,
  buildEmployeeFilterFields,
  getStatusFilterFromFilters,
} from '../utils/employeeAdvancedFilters';
import {
  type PayrollReportLayout,
  type PayrollReportDataRow,
  buildPayrollReportRows,
  formatPayrollReportCell,
  catalogFieldsForLayout,
  defaultOutputKeysForLayout,
  getDefaultLabelForPayrollOutputField,
  resolvePayPeriodType,
  resolvePayPeriodLabel,
  formatPayrollRunPeriodRow,
  parsePayrollRunFiltersSnapshot,
  PAYROLL_REPORT_OUTPUT_FIELD_CATALOG,
  type PayrollLineBreakdown,
} from '../utils/payrollReportOutputCatalog';
import {
  type PayrollBuilderFreqKind,
  type PayrollBuilderReportOptions,
  DEFAULT_PAYROLL_BUILDER_REPORT_OPTIONS,
  parsePayrollBuilderReportOptions,
  buildPayrollRunSnapshotFromEmployeeFilters,
  buildPayrollReportFiltersFromOptions,
  filterPayrollPreviewEmployees,
  normalizePayrollBuilderLayout,
  filterPayrollRunsForSearch,
  RUN_HISTORY_PAGE_SIZE,
} from '../utils/payrollBuilderFilters';
import {
  buildEmployeeLookupMaps,
  filtersUsePayRecords,
  loadPayrollReportEmployees,
  loadPayrollReportPayRecords,
  loadPayrollReportReferenceData,
} from '../utils/payrollReportBootstrap';
import {
  parseLocalDate,
  toLocalDateString,
  formatRangeDate,
  getPeriodEndForKind,
  getOohHourlyCycleContaining,
  normalizeFreqName,
} from '../utils/payrollPeriodLocal';
import { createCSVFile, createExcelFile, createPDFFile } from '../utils/reportExportFiles';
import { formatCompanyDateLocale, formatCompanyDateTime } from '../utils/companyDateFormat';

interface OutputColumnRow {
  id: string;
  fieldKey: string;
  headerLabel: string;
}

function newRowId(): string {
  return `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

function refreshFilterIds(filters: FilterCondition[]): FilterCondition[] {
  return filters.map((f) => ({ ...f, id: newRowId() }));
}

function parseEmployeeFiltersFromJson(json: string): FilterCondition[] {
  try {
    const a = JSON.parse(json) as FilterCondition[];
    return Array.isArray(a) ? refreshFilterIds(a) : [];
  } catch {
    return [];
  }
}

function defaultMandatoryFilters(): FilterCondition[] {
  return [
    { id: newRowId(), field: 'employeeReportStatus', value: 'Active' },
    { id: newRowId(), field: 'mainLocationId', value: '' },
  ];
}

function ensureLeadingMandatoryFilters(prev: FilterCondition[]): FilterCondition[] {
  const others = prev.filter((f) => f.field !== 'employeeReportStatus' && f.field !== 'mainLocationId');
  const statusRow = prev.find((f) => f.field === 'employeeReportStatus');
  const mainRow = prev.find((f) => f.field === 'mainLocationId');
  const status: FilterCondition = statusRow
    ? { ...statusRow, field: 'employeeReportStatus' }
    : { id: newRowId(), field: 'employeeReportStatus', value: 'Active' };
  let mainValue: string | number = '';
  if (mainRow) {
    const v = mainRow.value;
    if (v === null || v === undefined) mainValue = '';
    else if (typeof v === 'string' && v.trim() === '') mainValue = '';
    else mainValue = v as string | number;
  }
  const main: FilterCondition = mainRow
    ? { ...mainRow, field: 'mainLocationId', value: mainValue }
    : { id: newRowId(), field: 'mainLocationId', value: '' };
  return [status, main, ...others];
}

function parseColumnsFromJson(json: string): { fieldKey: string; headerLabel: string }[] {
  try {
    const a = JSON.parse(json) as { fieldKey: string; headerLabel: string }[];
    return Array.isArray(a) ? a : [];
  } catch {
    return [];
  }
}

function defaultOutputColumns(layout: PayrollReportLayout): OutputColumnRow[] {
  return defaultOutputKeysForLayout(layout).map((key) => ({
    id: newRowId(),
    fieldKey: key,
    headerLabel: getDefaultLabelForPayrollOutputField(key),
  }));
}

function resolvePaymentFrequencyIds(freqs: { id: number; name: string }[]) {
  const norm = (s: string) => normalizeFreqName(s);
  const weekly = freqs.find((f) => {
    const n = norm(f.name);
    return n === 'weekly' || n === 'week';
  });
  const monthly = freqs.find((f) => {
    const n = norm(f.name);
    return n === 'monthly' && !n.includes('invoice');
  });
  const fort = freqs.find((f) => {
    const n = norm(f.name);
    return n.includes('fortnight') || n.includes('bi-week') || n.includes('biweek');
  });
  return { weeklyId: weekly?.id ?? null, monthlyId: monthly?.id ?? null, fortnightlyId: fort?.id ?? null };
}

function formatYmd(iso: string): string {
  if (!iso) return '';
  return iso.slice(0, 10);
}

type PayrollRunJob = {
  id: string;
  kind: 'create' | 'finalize';
  status: 'pending' | 'processing' | 'completed' | 'failed';
  periodStart?: string;
  periodEnd?: string;
  freqKind?: PayrollBuilderFreqKind;
  runId?: number;
  error?: string;
  startedAt: Date;
};

const PayrollBuilderPage: React.FC = () => {
  const navigate = useNavigate();
  const weekStartsOn = useWeekStartDay();
  const { hasPermission } = usePermissions();
  const canForce = hasPermission('payroll_management');

  const [loading, setLoading] = useState(true);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [presetsLoading, setPresetsLoading] = useState(false);
  const [allEmployees, setAllEmployees] = useState<Employee[]>([]);
  const [roles, setRoles] = useState<{ id: number; name: string }[]>([]);
  const [locations, setLocations] = useState<{ id: number; name: string; isActive?: boolean }[]>([]);
  const [positions, setPositions] = useState<{ id: number; title: string; isActive?: boolean }[]>([]);
  const [payrollDepartments, setPayrollDepartments] = useState<
    { id: number; name: string; isActive?: boolean }[]
  >([]);
  const [schedules, setSchedules] = useState<{ id: number; name: string; isActive?: boolean }[]>([]);
  const [paymentTypes, setPaymentTypes] = useState<PaymentType[]>([]);
  const [paymentFrequencies, setPaymentFrequencies] = useState<{ id: number; name: string }[]>([]);
  const [freqIds, setFreqIds] = useState<{
    weeklyId: number | null;
    monthlyId: number | null;
    fortnightlyId: number | null;
  }>({ weeklyId: null, monthlyId: null, fortnightlyId: null });

  const [freqKind, setFreqKind] = useState<PayrollBuilderFreqKind>('weekly');
  const [enabledCycles, setEnabledCycles] = useState<PayrollCycleOption[]>(() => getEnabledPayrollCycleOptions());
  const [periodStartDate, setPeriodStartDate] = useState('');
  const [periodMonth, setPeriodMonth] = useState('');
  const [weekPickerOpen, setWeekPickerOpen] = useState(false);
  const [fortnightPickerOpen, setFortnightPickerOpen] = useState(false);
  const [hourlyCyclePickerOpen, setHourlyCyclePickerOpen] = useState(false);

  const [layout, setLayout] = useState<PayrollReportLayout>('detail');
  const [reportOptions, setReportOptions] = useState<PayrollBuilderReportOptions>(
    DEFAULT_PAYROLL_BUILDER_REPORT_OPTIONS
  );
  const [filters, setFilters] = useState<FilterCondition[]>(() => defaultMandatoryFilters());
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [columnsExpanded, setColumnsExpanded] = useState(true);
  const [outputColumns, setOutputColumns] = useState<OutputColumnRow[]>(() => defaultOutputColumns('detail'));

  const [templateList, setTemplateList] = useState<{ id: number; name: string; isDefault?: boolean }[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [loadedTemplateId, setLoadedTemplateId] = useState<number | null>(null);
  const [templateName, setTemplateName] = useState('');
  const [templateIsDefault, setTemplateIsDefault] = useState(false);
  const [templateSaving, setTemplateSaving] = useState(false);
  const loadedTemplateIdRef = useRef<number | null>(null);
  loadedTemplateIdRef.current = loadedTemplateId;

  const [filterEditModalOpen, setFilterEditModalOpen] = useState(false);
  const [filterModalDraft, setFilterModalDraft] = useState<FilterCondition[]>([]);
  const [filterModalSaving, setFilterModalSaving] = useState(false);
  const [columnLabelsModalOpen, setColumnLabelsModalOpen] = useState(false);
  const [columnModalDraft, setColumnModalDraft] = useState<OutputColumnRow[]>([]);
  const [columnModalAddFieldKey, setColumnModalAddFieldKey] = useState('');
  const [columnModalSaving, setColumnModalSaving] = useState(false);

  const [runs, setRuns] = useState<PayrollRun[]>([]);
  const [loadingRuns, setLoadingRuns] = useState(true);
  /** Soft refresh (poll / refresh with existing rows) — spins icon only, keeps table mounted. */
  const [refreshingRuns, setRefreshingRuns] = useState(false);
  const [historySearch, setHistorySearch] = useState('');
  const [historyPage, setHistoryPage] = useState(1);

  const [activeRunId, setActiveRunId] = useState<number | null>(null);
  const [preview, setPreview] = useState<PayrollRunPreviewDto | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewRows, setPreviewRows] = useState<PayrollReportDataRow[] | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewModalRun, setPreviewModalRun] = useState<PayrollRun | null>(null);
  const [historyAction, setHistoryAction] = useState<{
    runId: number;
    kind: 'view' | 'csv' | 'excel' | 'pdf';
  } | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const [runJobs, setRunJobs] = useState<PayrollRunJob[]>([]);
  const [forceFinalize, setForceFinalize] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [clearingHistory, setClearingHistory] = useState(false);

  const [nameModal, setNameModal] = useState<{
    title: string;
    confirmLabel?: string;
    initialValue?: string;
    onConfirm: (name: string) => void | Promise<void>;
  } | null>(null);
  const [messageDialog, setMessageDialog] = useState<MessageDialogOpen>(null);

  const [allPayRecords, setAllPayRecords] = useState<PayRecord[]>([]);

  const [worksNumberByEmployeeId, setWorksNumberByEmployeeId] = useState<Record<number, string>>({});
  const [firstNameByEmployeeId, setFirstNameByEmployeeId] = useState<Record<number, string>>({});
  const [lastNameByEmployeeId, setLastNameByEmployeeId] = useState<Record<number, string>>({});

  const employeesInflightRef = useRef<Promise<Employee[]> | null>(null);
  const payRecordsInflightRef = useRef<Promise<PayRecord[]> | null>(null);
  const allEmployeesRef = useRef<Employee[]>([]);
  const allPayRecordsRef = useRef<PayRecord[]>([]);
  allEmployeesRef.current = allEmployees;
  allPayRecordsRef.current = allPayRecords;

  const adHoc = loadedTemplateId == null;

  const filterFields = useMemo(
    (): FilterFieldOption[] =>
      buildEmployeeFilterFields({
        locations: locations as never,
        positions: positions as never,
        payrollDepartments: payrollDepartments as never,
        schedules: schedules as never,
        roles: roles as never,
        paymentTypes,
        paymentFrequencies: paymentFrequencies as never,
        leavePolicies: [],
      }),
    [locations, positions, payrollDepartments, schedules, roles, paymentTypes, paymentFrequencies]
  );

  const getWeekStart = useCallback(
    (date: Date): string => {
      const day = date.getDay();
      let diff = day - weekStartsOn;
      if (diff < 0) diff += 7;
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - diff);
      return toLocalDateString(weekStart);
    },
    [weekStartsOn]
  );

  const fortnightlyDateOptions = useMemo(() => {
    const options: { value: string; label: string }[] = [];
    const today = new Date();
    for (let m = -24; m <= 12; m++) {
      const base = new Date(today.getFullYear(), today.getMonth() + m, 1);
      for (const dayRangeStart of [1, 15]) {
        const endDay = Math.min(
          dayRangeStart + 6,
          new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate()
        );
        for (let day = dayRangeStart; day <= endDay; day++) {
          const d = new Date(base.getFullYear(), base.getMonth(), day);
          if (d.getDay() === weekStartsOn) {
            const ymd = toLocalDateString(d);
            options.push({
              value: ymd,
              label: formatCompanyDateLocale(d, {
                weekday: 'short',
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              }),
            });
            break;
          }
        }
      }
    }
    return options;
  }, [weekStartsOn]);

  const effectivePeriodStart = useMemo(() => {
    if (freqKind === 'ooh-hourly') {
      if (periodStartDate) return getOohHourlyCycleContaining(parseLocalDate(periodStartDate)).start;
      return getOohHourlyCycleContaining(new Date()).start;
    }
    if (freqKind === 'monthly' && periodMonth) return `${periodMonth}-01`;
    return periodStartDate;
  }, [freqKind, periodMonth, periodStartDate]);

  const effectivePeriodEnd = useMemo(() => {
    if (!effectivePeriodStart) return '';
    return getPeriodEndForKind(effectivePeriodStart, freqKind);
  }, [effectivePeriodStart, freqKind]);

  const weekRangeLabel = useMemo(() => {
    if (!periodStartDate || freqKind !== 'weekly') return '';
    const start = parseLocalDate(periodStartDate);
    const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
    return `${formatRangeDate(start)} - ${formatRangeDate(end)}`;
  }, [periodStartDate, freqKind]);

  const fortnightRangeLabel = useMemo(() => {
    if (!periodStartDate || freqKind !== 'fortnightly') return '';
    const start = parseLocalDate(periodStartDate);
    const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 13);
    return `${formatRangeDate(start)} - ${formatRangeDate(end)}`;
  }, [periodStartDate, freqKind]);

  const monthRangeLabel = useMemo(() => {
    if (!periodMonth || freqKind !== 'monthly') return '';
    const [y, mo] = periodMonth.split('-').map(Number);
    const start = new Date(y, mo - 1, 1);
    const end = new Date(y, mo, 0);
    return `${formatRangeDate(start)} - ${formatRangeDate(end)}`;
  }, [periodMonth, freqKind]);

  const oohHourlyRangeLabel = useMemo(() => {
    if (freqKind !== 'ooh-hourly' || !effectivePeriodStart || !effectivePeriodEnd) return '';
    return `${formatRangeDate(parseLocalDate(effectivePeriodStart))} - ${formatRangeDate(parseLocalDate(effectivePeriodEnd))}`;
  }, [freqKind, effectivePeriodStart, effectivePeriodEnd]);

  const periodSelectionLabel = useMemo(() => {
    if (freqKind === 'weekly') return weekRangeLabel || null;
    if (freqKind === 'fortnightly') return fortnightRangeLabel || null;
    if (freqKind === 'monthly') return monthRangeLabel || null;
    if (freqKind === 'ooh-hourly') return oohHourlyRangeLabel || null;
    return null;
  }, [freqKind, weekRangeLabel, fortnightRangeLabel, monthRangeLabel, oohHourlyRangeLabel]);

  const activeRun = useMemo(() => runs.find((r) => r.id === activeRunId) ?? null, [runs, activeRunId]);

  const reportFilters = useMemo(
    () => buildPayrollReportFiltersFromOptions(activeRunId, reportOptions),
    [activeRunId, reportOptions]
  );

  const filteredRuns = useMemo(
    () => filterPayrollRunsForSearch(runs, historySearch),
    [runs, historySearch]
  );

  const historyTotalPages = Math.max(1, Math.ceil(filteredRuns.length / RUN_HISTORY_PAGE_SIZE));

  const paginatedRuns = useMemo(() => {
    const page = Math.min(historyPage, historyTotalPages);
    const start = (page - 1) * RUN_HISTORY_PAGE_SIZE;
    return filteredRuns.slice(start, start + RUN_HISTORY_PAGE_SIZE);
  }, [filteredRuns, historyPage, historyTotalPages]);

  const activeRunJobs = useMemo(
    () => runJobs.filter((j) => j.status !== 'completed'),
    [runJobs]
  );

  const isCreateJobRunning = useMemo(
    () =>
      runJobs.some(
        (j) => j.kind === 'create' && (j.status === 'pending' || j.status === 'processing')
      ),
    [runJobs]
  );

  const isFinalizeJobRunning = useMemo(
    () =>
      runJobs.some(
        (j) => j.kind === 'finalize' && (j.status === 'pending' || j.status === 'processing')
      ),
    [runJobs]
  );

  const getRunRowStatus = useCallback(
    (
      r: PayrollRun
    ): 'pending' | 'processing' | 'failed' | 'draft' | 'finalized' | 'readyforview' => {
      const job = runJobs.find((j) => j.runId === r.id && j.status !== 'completed');
      if (job?.status === 'pending' || job?.status === 'processing') return job.status;
      if (job?.status === 'failed') return 'failed';
      const s = String(r.status || '')
        .toLowerCase()
        .replace(/\s+/g, '');
      if (s === 'finalized') return 'finalized';
      if (s === 'readyforview') return 'readyforview';
      if (s === 'processing') return 'processing';
      if (s === 'failed') return 'failed';
      return 'draft';
    },
    [runJobs]
  );

  const patchRunJob = useCallback((jobId: string, patch: Partial<PayrollRunJob>) => {
    setRunJobs((prev) => prev.map((j) => (j.id === jobId ? { ...j, ...patch } : j)));
  }, []);

  const removeRunJob = useCallback((jobId: string) => {
    setRunJobs((prev) => prev.filter((j) => j.id !== jobId));
  }, []);

  const applyEmployeeMaps = useCallback((employees: Employee[]) => {
    const maps = buildEmployeeLookupMaps(employees);
    setWorksNumberByEmployeeId(maps.worksNumberByEmployeeId);
    setFirstNameByEmployeeId(maps.firstNameByEmployeeId);
    setLastNameByEmployeeId(maps.lastNameByEmployeeId);
  }, []);

  const ensureEmployees = useCallback(async (): Promise<Employee[]> => {
    if (allEmployeesRef.current.length > 0) return allEmployeesRef.current;
    if (employeesInflightRef.current) return employeesInflightRef.current;
    setEmployeesLoading(true);
    const promise = loadPayrollReportEmployees()
      .then((data) => {
        allEmployeesRef.current = data;
        setAllEmployees(data);
        applyEmployeeMaps(data);
        return data;
      })
      .finally(() => {
        employeesInflightRef.current = null;
        setEmployeesLoading(false);
      });
    employeesInflightRef.current = promise;
    return promise;
  }, [applyEmployeeMaps]);

  const ensurePayRecordsForFilters = useCallback(async (): Promise<PayRecord[]> => {
    if (!filtersUsePayRecords(filters)) return allPayRecordsRef.current;
    if (allPayRecordsRef.current.length > 0) return allPayRecordsRef.current;
    if (payRecordsInflightRef.current) return payRecordsInflightRef.current;
    const promise = loadPayrollReportPayRecords()
      .then((data) => {
        allPayRecordsRef.current = data;
        setAllPayRecords(data);
        return data;
      })
      .finally(() => {
        payRecordsInflightRef.current = null;
      });
    payRecordsInflightRef.current = promise;
    return promise;
  }, [filters]);

  useEffect(() => {
    setHistoryPage(1);
  }, [historySearch]);

  useEffect(() => {
    if (historyPage > historyTotalPages) setHistoryPage(historyTotalPages);
  }, [historyPage, historyTotalPages]);

  const showAlert = (title: string, message: string, tone?: 'info' | 'success' | 'error') => {
    setMessageDialog({ variant: 'alert', title, message, tone });
  };

  const showConfirm = (
    title: string,
    message: string,
    onConfirm: () => void,
    options?: {
      confirmLabel?: string;
      cancelLabel?: string;
      danger?: boolean;
      secondaryLabel?: string;
      onSecondary?: () => void;
    }
  ) => {
    setMessageDialog({
      variant: 'confirm',
      title,
      message,
      onConfirm,
      confirmLabel: options?.confirmLabel ?? 'Delete',
      cancelLabel: options?.cancelLabel,
      tone: options?.danger ? 'danger' : 'default',
      secondaryLabel: options?.secondaryLabel,
      onSecondary: options?.onSecondary,
    });
  };

  const normalizeRunStatus = (status?: string | null) =>
    String(status || '')
      .toLowerCase()
      .replace(/\s+/g, '');

  const isRunFinalized = (status?: string | null) => normalizeRunStatus(status) === 'finalized';
  const isRunReadyForView = (status?: string | null) => normalizeRunStatus(status) === 'readyforview';
  const isRunProcessing = (status?: string | null) => normalizeRunStatus(status) === 'processing';
  const isRunFailed = (status?: string | null) => normalizeRunStatus(status) === 'failed';

  const formatPreviewProcessedAt = (iso?: string | null) => {
    if (!iso) return 'unknown time';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : formatCompanyDateTime(d);
  };

  const columnsPayload = useCallback(
    () => outputColumns.map((c) => ({ fieldKey: c.fieldKey, headerLabel: c.headerLabel })),
    [outputColumns]
  );

  const refreshTemplates = useCallback(async () => {
    const rows = await payrollBuilderReportTemplatesApi.list();
    setTemplateList(rows.map((r) => ({ id: r.id, name: r.name, isDefault: r.isDefault })));
    return rows;
  }, []);

  const loadRuns = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent === true;
    // Keep the table mounted during background polls / soft refresh so the panel does not jump.
    if (silent) setRefreshingRuns(true);
    else setLoadingRuns(true);
    try {
      const list = await payrollRunApi.list(50);
      setRuns(list);
    } catch (e) {
      console.error(e);
    } finally {
      if (silent) setRefreshingRuns(false);
      else setLoadingRuns(false);
    }
  }, []);

  const hasProcessingRuns = useMemo(
    () => runs.some((r) => String(r.status || '').toLowerCase().replace(/\s+/g, '') === 'processing'),
    [runs]
  );

  useEffect(() => {
    if (!hasProcessingRuns) return;
    const timer = window.setInterval(() => {
      void loadRuns({ silent: true });
    }, 5000);
    return () => window.clearInterval(timer);
  }, [hasProcessingRuns, loadRuns]);

  const applyTemplateDto = useCallback((d: PayrollBuilderReportTemplateDto) => {
    const nextLayout = normalizePayrollBuilderLayout(d.layout);
    setLayout(nextLayout);
    const parsedFilters = parseEmployeeFiltersFromJson(d.employeeFiltersJson);
    setFilters(parsedFilters.length > 0 ? ensureLeadingMandatoryFilters(parsedFilters) : defaultMandatoryFilters());
    setReportOptions(parsePayrollBuilderReportOptions(d.reportOptionsJson));
    const cols = parseColumnsFromJson(d.columnsJson);
    setOutputColumns(
      cols.length > 0
        ? cols.map((c) => ({
            id: newRowId(),
            fieldKey: c.fieldKey,
            headerLabel: c.headerLabel || getDefaultLabelForPayrollOutputField(c.fieldKey),
          }))
        : defaultOutputColumns(nextLayout)
    );
    setTemplateName(d.name);
    setTemplateIsDefault(Boolean(d.isDefault));
    setLoadedTemplateId(d.id);
    setSelectedTemplateId(String(d.id));
    setPreviewRows(null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [ref, templates, runList, companySettings] = await Promise.all([
          loadPayrollReportReferenceData(),
          payrollBuilderReportTemplatesApi.list(),
          payrollRunApi.list(50),
          companySettingsApi.get().catch(() => null),
        ]);
        if (cancelled) return;
        setRoles(ref.roles);
        setLocations(ref.locations);
        setPositions(ref.positions);
        setPayrollDepartments(ref.payrollDepartments);
        setSchedules(ref.schedules);
        setPaymentTypes(ref.paymentTypes);
        const freqs = ref.paymentFrequencies;
        setPaymentFrequencies(freqs);
        setFreqIds(resolvePaymentFrequencyIds(freqs));
        setEnabledCycles(getEnabledPayrollCycleOptions(companySettings));
        setTemplateList(templates.map((r) => ({ id: r.id, name: r.name, isDefault: r.isDefault })));
        setRuns(runList);
        const defaultTemplate = templates.find((t) => t.isDefault);
        if (defaultTemplate) {
          try {
            const full = await payrollBuilderReportTemplatesApi.get(defaultTemplate.id);
            if (!cancelled) applyTemplateDto(full);
          } catch (e) {
            console.error(e);
          }
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) setPreviewError('Failed to load reference data.');
      } finally {
        if (!cancelled) {
          setLoading(false);
          setLoadingRuns(false);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const today = new Date();
    setPeriodStartDate(getWeekStart(today));
    setPeriodMonth(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`);
  }, [getWeekStart]);

  useEffect(() => {
    if (enabledCycles.length > 0 && !enabledCycles.some((c) => c.kind === freqKind)) {
      setFreqKind(enabledCycles[0].kind);
    }
  }, [enabledCycles, freqKind]);

  const skipFreqKindReset = useRef(true);
  useEffect(() => {
    if (skipFreqKindReset.current) {
      skipFreqKindReset.current = false;
      return;
    }
    setWeekPickerOpen(false);
    setFortnightPickerOpen(false);
    setHourlyCyclePickerOpen(false);
    const today = new Date();
    if (freqKind === 'monthly') {
      setPeriodMonth(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`);
    } else if (freqKind === 'fortnightly') {
      const ws = getWeekStart(today);
      const nearest =
        fortnightlyDateOptions.find((o) => o.value >= ws) ||
        fortnightlyDateOptions[fortnightlyDateOptions.length - 1];
      if (nearest) setPeriodStartDate(nearest.value);
    } else if (freqKind === 'ooh-hourly') {
      setPeriodStartDate(getOohHourlyCycleContaining(today).start);
    } else {
      setPeriodStartDate(getWeekStart(today));
    }
  }, [freqKind, getWeekStart, fortnightlyDateOptions]);

  const loadPreview = useCallback(async (runId: number) => {
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const p = await payrollRunApi.preview(runId);
      setPreview(p);
      return p;
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      setPreviewError(msg || 'Failed to load preview.');
      setPreview(null);
      throw e;
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const activeRunStatusKey = normalizeRunStatus(activeRun?.status);

  useEffect(() => {
    if (activeRunId == null) {
      setPreview(null);
      setPreviewRows(null);
      setPreviewError(null);
      return;
    }
    if (activeRunStatusKey !== 'readyforview' && activeRunStatusKey !== 'finalized') {
      setPreview(null);
      setPreviewRows(null);
      if (activeRunStatusKey === 'failed') {
        setPreviewError(
          activeRun?.previewProcessingError ||
            'Payroll preview processing failed. Use View to re-run processing.'
        );
      } else {
        setPreviewError(null);
      }
      setPreviewLoading(false);
      return;
    }

    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    void (async () => {
      try {
        const p = await payrollRunApi.preview(activeRunId);
        if (cancelled) return;
        setPreview(p);
        void ensureEmployees().catch(() => undefined);
      } catch (e: unknown) {
        if (cancelled) return;
        const msg =
          e && typeof e === 'object' && 'response' in e
            ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
            : null;
        setPreviewError(msg || 'Failed to load preview.');
        setPreview(null);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    activeRunId,
    activeRunStatusKey,
    activeRun?.previewProcessingError,
    ensureEmployees,
  ]);

  useEffect(() => {
    if (activeRunId == null || !preview || !filtersUsePayRecords(filters)) return;
    void ensurePayRecordsForFilters().catch(() => undefined);
  }, [activeRunId, preview, filters, ensurePayRecordsForFilters]);

  const syncFiltersFromRunSnapshot = useCallback((run: PayrollRun) => {
    const snap = parsePayrollRunFiltersSnapshot(run);
    if (!snap) return;
    setFilters((prev) => {
      const next = ensureLeadingMandatoryFilters(prev);
      return next.map((f) => {
        if (f.field === 'mainLocationId' && snap.mainLocationId != null) {
          return { ...f, value: snap.mainLocationId };
        }
        return f;
      });
    });
  }, []);

  const buildReportRowsFromPreview = useCallback(
    (p: PayrollRunPreviewDto) => {
      // Ready/Finalized drafts already contain the run membership. Re-applying the
      // live builder filters (especially mainLocationId left on KDOC) blanks DubDoc/TDOC views.
      const trustStoredMembership =
        isRunReadyForView(activeRun?.status) || isRunFinalized(activeRun?.status);
      let employeeResults = p.employees;
      if (!trustStoredMembership && allEmployeesRef.current.length > 0) {
        employeeResults = filterPayrollPreviewEmployees({
          preview: p,
          employeeFilters: filters,
          filterFields,
          allEmployees: allEmployeesRef.current,
          allPayRecords: allPayRecordsRef.current,
        });
      }
      const narrowed: PayrollRunPreviewDto = { ...p, employees: employeeResults };
      return buildPayrollReportRows(narrowed, layout, reportFilters, activeRun);
    },
    [filters, filterFields, layout, reportFilters, activeRun, allEmployees, allPayRecords]
  );

  useEffect(() => {
    if (!preview || activeRunId == null) {
      setPreviewRows(null);
      return;
    }
    setPreviewRows(buildReportRowsFromPreview(preview));
  }, [preview, activeRunId, buildReportRowsFromPreview]);

  const triggerFileDownload = (url: string, filename: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    if (url.startsWith('blob:')) URL.revokeObjectURL(url);
  };

  const payrollExportBase = (run: PayrollRun) =>
    `Payroll_Run_${run.id}_${formatYmd(run.periodStart)}`;

  const loadAndBuildRowsForRun = useCallback(
    async (run: PayrollRun) => {
      const p = await payrollRunApi.preview(run.id);
      void ensureEmployees().catch(() => undefined);

      const trustStoredMembership =
        isRunReadyForView(run.status) || isRunFinalized(run.status);
      let employeeResults = p.employees;
      if (!trustStoredMembership && allEmployeesRef.current.length > 0) {
        employeeResults = filterPayrollPreviewEmployees({
          preview: p,
          employeeFilters: filters,
          filterFields,
          allEmployees: allEmployeesRef.current,
          allPayRecords: allPayRecordsRef.current,
        });
      }

      const narrowed: PayrollRunPreviewDto = { ...p, employees: employeeResults };
      const runReportFilters = buildPayrollReportFiltersFromOptions(run.id, reportOptions);
      const rows = buildPayrollReportRows(narrowed, layout, runReportFilters, run);
      return { preview: p, rows };
    },
    [filters, filterFields, layout, reportOptions, ensureEmployees]
  );

  const reprocessRun = useCallback(
    async (run: PayrollRun) => {
      try {
        await payrollRunApi.reprocess(run.id);
        setPreview(null);
        setPreviewRows(null);
        setPreviewModalOpen(false);
        setStatusMessage(
          `Re-run queued for run #${run.id}. Check back when status is Ready for View.`
        );
        await loadRuns();
        setActiveRunId(run.id);
      } catch (err: unknown) {
        const msg =
          err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
            : err instanceof Error
              ? err.message
              : 'Could not re-run processing.';
        showAlert('Re-run failed', msg || 'Could not re-run processing.', 'error');
      }
    },
    [loadRuns]
  );

  const openStoredRunReport = useCallback(
    async (run: PayrollRun) => {
      setHistoryAction({ runId: run.id, kind: 'view' });
      try {
        syncFiltersFromRunSnapshot(run);
        const { preview: p, rows } = await loadAndBuildRowsForRun(run);
        if (rows.length === 0) {
          showAlert('No data', 'No payroll rows are stored on this run.', 'info');
          return;
        }
        setActiveRunId(run.id);
        setPreview(p);
        setPreviewRows(rows);
        setPreviewModalRun(run);
        setPreviewModalOpen(true);
      } catch (err: unknown) {
        const status =
          err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { status?: number; data?: { message?: string } } }).response?.status
            : undefined;
        const msg =
          err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
            : err instanceof Error
              ? err.message
              : 'Could not load payroll data for this run.';
        if (status === 409) {
          showAlert('Not ready yet', msg || 'Payroll records are still processing.', 'info');
          await loadRuns();
          return;
        }
        showAlert('Load failed', msg || 'Could not load payroll data for this run.', 'error');
      } finally {
        setHistoryAction(null);
      }
    },
    [loadAndBuildRowsForRun, loadRuns, syncFiltersFromRunSnapshot]
  );

  const buildExportTableForRun = useCallback(
    (p: PayrollRunPreviewDto, rows: PayrollReportDataRow[], run: PayrollRun) => {
      const headers = outputColumns.map(
        (c) => c.headerLabel.trim() || getDefaultLabelForPayrollOutputField(c.fieldKey)
      );
      const runPayPeriodType = resolvePayPeriodType(run);
      const runPayPeriodLabel = resolvePayPeriodLabel(run);
      let previousEmployeeId: number | null = null;
      const dataRows = rows.map((row) => {
        const cells = outputColumns.map((col) =>
          String(
            formatPayrollReportCell(row, col.fieldKey, {
              preview: p,
              runStatus: run.status ?? null,
              worksNumberByEmployeeId,
              firstNameByEmployeeId,
              lastNameByEmployeeId,
              payPeriodType: runPayPeriodType,
              payPeriodLabel: runPayPeriodLabel,
              groupByEmployee: false,
              previousEmployeeId,
            }) ?? ''
          )
        );
        previousEmployeeId = row.employee.employeeId;
        return cells;
      });
      return { headers, dataRows };
    },
    [
      outputColumns,
      worksNumberByEmployeeId,
      firstNameByEmployeeId,
      lastNameByEmployeeId,
    ]
  );

  const openRunReportView = useCallback(
    async (run: PayrollRun, e?: React.MouseEvent) => {
      e?.stopPropagation();

      if (isRunProcessing(run.status)) {
        showAlert(
          'Still processing',
          'Payroll records are being processed in the background. Check back when status is Ready for View.',
          'info'
        );
        return;
      }

      if (isRunFailed(run.status)) {
        showConfirm(
          'Processing failed',
          run.previewProcessingError ||
            'Payroll preview processing failed. Re-run processing to try again.',
          () => {
            void reprocessRun(run);
          },
          { confirmLabel: 'Re-run processing', cancelLabel: 'Close' }
        );
        return;
      }

      if (isRunReadyForView(run.status)) {
        if (activeRunId === run.id && preview && previewRows && previewRows.length > 0) {
          setPreviewModalRun(run);
          setPreviewModalOpen(true);
          return;
        }
        showConfirm(
          'Payroll records ready',
          `Records for run #${run.id} were created at ${formatPreviewProcessedAt(run.previewProcessedAt)}.\n\nView the existing records, or re-run processing to recalculate.`,
          () => {
            void openStoredRunReport(run);
          },
          {
            confirmLabel: 'View existing',
            cancelLabel: 'Cancel',
            secondaryLabel: 'Re-run processing',
            onSecondary: () => {
              void reprocessRun(run);
            },
          }
        );
        return;
      }

      if (activeRunId === run.id && preview && previewRows && previewRows.length > 0) {
        setPreviewModalRun(run);
        setPreviewModalOpen(true);
        return;
      }

      await openStoredRunReport(run);
    },
    [
      activeRunId,
      preview,
      previewRows,
      openStoredRunReport,
      reprocessRun,
      isRunProcessing,
      isRunFailed,
      isRunReadyForView,
      formatPreviewProcessedAt,
    ]
  );

  const exportRunReport = useCallback(
    async (run: PayrollRun, kind: 'csv' | 'excel' | 'pdf', e?: React.MouseEvent) => {
      e?.stopPropagation();
      if (isRunProcessing(run.status)) {
        showAlert(
          'Still processing',
          'Payroll records are being processed in the background. Check back when status is Ready for View.',
          'info'
        );
        return;
      }
      if (isRunFailed(run.status)) {
        showAlert(
          'Not ready',
          run.previewProcessingError ||
            'Payroll preview processing failed. Re-run processing from View before exporting.',
          'error'
        );
        return;
      }
      setHistoryAction({ runId: run.id, kind });
      try {
        const { preview: p, rows } =
          activeRunId === run.id && previewRows && preview
            ? { preview, rows: previewRows }
            : await loadAndBuildRowsForRun(run);
        if (rows.length === 0) {
          showAlert('No data', 'No rows match the current filters for this run.', 'info');
          return;
        }
        const { headers, dataRows } = buildExportTableForRun(p, rows, run);
        const base = payrollExportBase(run);
        if (kind === 'csv') {
          const blob = createCSVFile(headers, dataRows, base);
          triggerFileDownload(blob.url, blob.filename);
        } else if (kind === 'excel') {
          const blob = createExcelFile(headers, dataRows, base);
          triggerFileDownload(blob.url, blob.filename);
        } else {
          const pdf = createPDFFile(headers, dataRows, base, { title: 'Payroll report' });
          triggerFileDownload(pdf.url, pdf.filename);
        }
      } catch (err: unknown) {
        const status =
          err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { status?: number; data?: { message?: string } } }).response?.status
            : undefined;
        const msg =
          err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
            : err instanceof Error
              ? err.message
              : 'Could not export this run.';
        if (status === 409) {
          showAlert('Not ready yet', msg || 'Payroll records are still processing.', 'info');
          await loadRuns();
          return;
        }
        showAlert('Export failed', msg || 'Could not export this run.', 'error');
      } finally {
        setHistoryAction(null);
      }
    },
    [
      activeRunId,
      previewRows,
      preview,
      loadAndBuildRowsForRun,
      buildExportTableForRun,
      isRunProcessing,
      isRunFailed,
      loadRuns,
    ]
  );

  const handleRunRowClick = (run: PayrollRun) => {
    syncFiltersFromRunSnapshot(run);
    setActiveRunId(run.id);
  };

  const saveTemplateBody = (
    name: string,
    overrides?: {
      employeeFilters?: FilterCondition[];
      outputColumns?: OutputColumnRow[];
      layout?: PayrollReportLayout;
      reportOptions?: PayrollBuilderReportOptions;
    }
  ) => {
    const effFilters = overrides?.employeeFilters ?? filters;
    const effColumns = overrides?.outputColumns ?? outputColumns;
    const effLayout = overrides?.layout ?? layout;
    const effReportOptions = overrides?.reportOptions ?? reportOptions;
    return {
      name,
      statusFilter: getStatusFilterFromFilters(effFilters),
      layout: effLayout,
      employeeFilters: effFilters as unknown[],
      reportOptions: effReportOptions as unknown as Record<string, unknown>,
      columns: effColumns.map((c) => ({ fieldKey: c.fieldKey, headerLabel: c.headerLabel })),
      isDefault: templateIsDefault,
    };
  };

  const persistTemplate = async (
    name: string,
    existingId?: number | null,
    overrides?: Parameters<typeof saveTemplateBody>[1]
  ) => {
    const trimmed = name.trim();
    if (!trimmed) {
      showAlert('Name required', 'Enter a template name before saving.', 'info');
      return;
    }
    setTemplateSaving(true);
    try {
      const body = saveTemplateBody(trimmed, overrides);
      const saved =
        existingId != null
          ? await payrollBuilderReportTemplatesApi.update(existingId, body)
          : await payrollBuilderReportTemplatesApi.create(body);
      await refreshTemplates();
      applyTemplateDto(saved);
      showAlert('Saved', existingId != null ? 'Report template updated.' : 'Report template saved.', 'success');
    } catch (e) {
      console.error(e);
      showAlert('Save failed', 'Could not save the report template.', 'error');
    } finally {
      setTemplateSaving(false);
    }
  };

  const clearTemplateSelection = () => {
    setSelectedTemplateId('');
    setLoadedTemplateId(null);
    setTemplateName('');
    setTemplateIsDefault(false);
    setLayout('detail');
    setFilters(defaultMandatoryFilters());
    setReportOptions(DEFAULT_PAYROLL_BUILDER_REPORT_OPTIONS);
    setOutputColumns(defaultOutputColumns('detail'));
    setPreviewRows(null);
  };

  const handleTemplateSelectChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const v = e.target.value;
    const previousLoadedId = loadedTemplateIdRef.current;
    if (!v) {
      clearTemplateSelection();
      return;
    }
    setSelectedTemplateId(v);
    try {
      const d = await payrollBuilderReportTemplatesApi.get(Number(v));
      applyTemplateDto(d);
    } catch (err) {
      console.error(err);
      showAlert('Load failed', 'Could not load the report template.', 'error');
      if (previousLoadedId != null) {
        setSelectedTemplateId(String(previousLoadedId));
        try {
          const d = await payrollBuilderReportTemplatesApi.get(previousLoadedId);
          applyTemplateDto(d);
        } catch {
          clearTemplateSelection();
        }
      } else {
        setSelectedTemplateId('');
      }
    }
  };

  const openCreateTemplateModal = (initialName: string) => {
    setNameModal({
      title: 'Name for report template',
      confirmLabel: 'Save',
      initialValue: initialName,
      onConfirm: async (name) => {
        await persistTemplate(name, null);
      },
    });
  };

  const handleSaveTemplate = () => {
    if (loadedTemplateId != null) {
      void persistTemplate(templateName, loadedTemplateId);
      return;
    }
    const name = templateName.trim();
    if (name) {
      void persistTemplate(name, null);
      return;
    }
    openCreateTemplateModal('');
  };

  const handleSaveAsNewTemplate = () => {
    const name = templateName.trim();
    if (name) {
      void persistTemplate(name, null);
      return;
    }
    setNameModal({
      title: 'Name for new report template',
      confirmLabel: 'Save',
      initialValue: '',
      onConfirm: async (newName) => {
        await persistTemplate(newName, null);
      },
    });
  };

  const handleDeleteTemplate = () => {
    if (!selectedTemplateId) {
      showAlert('Select a template', 'Choose a saved report template to delete.', 'info');
      return;
    }
    const id = Number(selectedTemplateId);
    const name = templateList.find((x) => x.id === id)?.name ?? 'this template';
    showConfirm(
      'Delete template',
      `Delete saved report template "${name}"? This cannot be undone.`,
      () => {
        void (async () => {
          try {
            await payrollBuilderReportTemplatesApi.delete(id);
            await refreshTemplates();
            if (loadedTemplateId === id) clearTemplateSelection();
            else setSelectedTemplateId('');
            showAlert('Deleted', 'The template was removed.', 'success');
          } catch (e) {
            console.error(e);
            showAlert('Delete failed', 'Could not delete the template.', 'error');
          }
        })();
      },
      { danger: true, confirmLabel: 'Delete', cancelLabel: 'Cancel' }
    );
  };

  const openFilterEditModal = () => {
    setFilterModalDraft(ensureLeadingMandatoryFilters(filters.map((f) => ({ ...f }))));
    setFilterEditModalOpen(true);
  };

  const handleSaveFilterModal = async () => {
    const next = ensureLeadingMandatoryFilters(filterModalDraft);
    setFilters(next);
    if (loadedTemplateId != null) {
      setFilterModalSaving(true);
      try {
        await persistTemplate(templateName, loadedTemplateId, { employeeFilters: next });
      } finally {
        setFilterModalSaving(false);
      }
    }
    setFilterEditModalOpen(false);
  };

  const openColumnModal = () => {
    setColumnModalDraft(outputColumns.map((c) => ({ ...c })));
    setColumnModalAddFieldKey('');
    setColumnLabelsModalOpen(true);
  };

  const handleSaveColumnModal = async () => {
    if (columnModalDraft.length === 0) {
      showAlert('No columns', 'Add at least one output column.', 'error');
      return;
    }
    setOutputColumns(columnModalDraft.map((c) => ({ ...c })));
    if (loadedTemplateId != null) {
      setColumnModalSaving(true);
      try {
        await persistTemplate(templateName, loadedTemplateId, { outputColumns: columnModalDraft });
      } finally {
        setColumnModalSaving(false);
      }
    }
    setColumnLabelsModalOpen(false);
  };

  const handleCreateDraft = () => {
    if (!effectivePeriodStart || !effectivePeriodEnd) {
      showAlert('Period required', 'Select a pay period before creating a draft.', 'error');
      return;
    }
    if (outputColumns.length === 0) {
      showAlert('No columns', 'Add at least one output column.', 'error');
      return;
    }
    setStatusMessage(null);

    const jobId = `pb-create-${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    const job: PayrollRunJob = {
      id: jobId,
      kind: 'create',
      status: 'pending',
      periodStart: effectivePeriodStart,
      periodEnd: effectivePeriodEnd,
      freqKind,
      startedAt: new Date(),
    };
    setRunJobs((prev) => [job, ...prev]);

    window.setTimeout(() => {
      patchRunJob(jobId, { status: 'processing' });
      void (async () => {
        try {
          const [employees, payRecords] = await Promise.all([
            ensureEmployees(),
            ensurePayRecordsForFilters(),
          ]);
          const snapshot = buildPayrollRunSnapshotFromEmployeeFilters({
            employeeFilters: filters,
            filterFields,
            freqKind,
            periodSelectionLabel,
            payrollDepartments: payrollDepartments as never,
            locations: locations as never,
            schedules: schedules as never,
            positions: positions as never,
            paymentFrequencies: paymentFrequencies as never,
            freqIds,
            allEmployees: employees,
            allPayRecords: payRecords,
          });
          const run = await payrollRunApi.create({
            periodStart: effectivePeriodStart,
            periodEnd: effectivePeriodEnd,
            notes: templateName.trim() || undefined,
            previewFiltersJson: JSON.stringify(snapshot),
          });
          await loadRuns();
          setActiveRunId(run.id);
          setPreview(null);
          setPreviewRows(null);
          setPreviewError(null);
          setStatusMessage(
            `Run #${run.id} created. Record processing is running in the background — check back when status is Ready for View. You can leave this page or start another report.`
          );
          removeRunJob(jobId);
        } catch (e: unknown) {
          const msg =
            e && typeof e === 'object' && 'response' in e
              ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
              : 'Could not create draft run.';
          patchRunJob(jobId, { status: 'failed', error: msg || 'Could not create draft run.' });
          showAlert('Create failed', msg || 'Could not create draft run.', 'error');
        }
      })();
    }, 0);
  };

  const handleFinalize = () => {
    if (activeRunId == null || !preview) return;
    if (forceFinalize && !canForce) return;
    if (forceFinalize && !preview.hasHourlyMismatchBlocker) return;

    setStatusMessage(null);
    const runId = activeRunId;
    const shouldForce = forceFinalize && preview.hasHourlyMismatchBlocker;

    const jobId = `pb-finalize-${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    const job: PayrollRunJob = {
      id: jobId,
      kind: 'finalize',
      status: 'pending',
      runId,
      startedAt: new Date(),
    };
    setRunJobs((prev) => [job, ...prev]);

    window.setTimeout(() => {
      patchRunJob(jobId, { status: 'processing' });
      void (async () => {
        try {
          await payrollRunApi.finalize(runId, shouldForce);
          setForceFinalize(false);
          await loadRuns();
          await loadPreview(runId);
          setStatusMessage('Run finalized successfully.');
          removeRunJob(jobId);
        } catch (e: unknown) {
          const status =
            e && typeof e === 'object' && 'response' in e
              ? (e as { response?: { status?: number; data?: { message?: string } } }).response?.status
              : undefined;
          const msg =
            e && typeof e === 'object' && 'response' in e
              ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
              : null;
          patchRunJob(jobId, { status: 'failed', error: msg || 'Finalize failed.', runId });
          if (status === 403) {
            showAlert('Permission denied', 'You do not have permission to force-finalize.', 'error');
          } else {
            showAlert('Finalize failed', msg || 'Finalize failed.', 'error');
          }
        }
      })();
    }, 0);
  };

  const handleDeleteRun = async (e: React.MouseEvent, r: PayrollRun) => {
    e.stopPropagation();
    if (isRunFinalized(r.status)) return;
    if (!window.confirm(`Delete payroll run #${r.id}? This cannot be undone.`)) return;
    setDeletingId(r.id);
    try {
      await payrollRunApi.delete(r.id);
      setRuns((prev) => prev.filter((x) => x.id !== r.id));
      if (activeRunId === r.id) {
        setActiveRunId(null);
        setPreview(null);
        setPreviewRows(null);
        setPreviewModalOpen(false);
        setPreviewModalRun(null);
      }
      void loadRuns().catch(() => undefined);
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      showAlert('Delete failed', msg || 'Could not delete the draft run.', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const handleClearAllHistory = async () => {
    const draftCount = runs.filter((r) => !isRunFinalized(r.status)).length;
    const finalizedCount = runs.length - draftCount;
    if (runs.length === 0) return;

    const message =
      draftCount === 0
        ? 'There are no non-finalized runs to delete. Finalized payroll runs cannot be removed from history.'
        : finalizedCount > 0
          ? `Delete all ${draftCount} non-finalized run${draftCount === 1 ? '' : 's'}? ${finalizedCount} finalized run${finalizedCount === 1 ? '' : 's'} will remain. This cannot be undone.`
          : `Delete all ${draftCount} non-finalized run${draftCount === 1 ? '' : 's'}? This cannot be undone.`;

    if (!window.confirm(message)) return;

    if (draftCount === 0) return;

    setClearingHistory(true);
    setStatusMessage(null);
    try {
      const { deletedCount } = await payrollRunApi.deleteAllDrafts();
      if (activeRunId != null && runs.some((r) => r.id === activeRunId && !isRunFinalized(r.status))) {
        setActiveRunId(null);
        setPreview(null);
        setPreviewRows(null);
      }
      const list = await payrollRunApi.list(50);
      setRuns(list);
      const remainingFinalized = list.filter((r) => isRunFinalized(r.status)).length;
      setStatusMessage(
        remainingFinalized > 0
          ? `Removed ${deletedCount} run${deletedCount === 1 ? '' : 's'}. ${remainingFinalized} finalized run${remainingFinalized === 1 ? '' : 's'} remain in history.`
          : `Removed ${deletedCount} run${deletedCount === 1 ? '' : 's'}.`
      );
    } catch {
      showAlert('Clear failed', 'Could not clear draft run history.', 'error');
    } finally {
      setClearingHistory(false);
    }
  };

  const handlePreviewReport = () => {
    if (activeRunId == null || !preview) {
      showAlert('No draft', 'Select a payroll run to preview report data.', 'info');
      return;
    }
    if (!previewRows || previewRows.length === 0) {
      showAlert('No data', 'No rows match the current filters for this run.', 'info');
      return;
    }
    setPreviewModalRun(activeRun);
    setPreviewModalOpen(true);
  };

  const addableColumnFields = useMemo(() => {
    const used = new Set(columnModalDraft.map((c) => c.fieldKey));
    return catalogFieldsForLayout(layout).filter((f) => !used.has(f.key));
  }, [columnModalDraft, layout]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-neutral-500 p-8">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="w-full max-w-none space-y-6 pb-4">
      <MessageDialog state={messageDialog} onClose={() => setMessageDialog(null)} />
      {nameModal && (
        <NamePromptModal
          open
          title={nameModal.title}
          confirmLabel={nameModal.confirmLabel}
          initialValue={nameModal.initialValue}
          onConfirm={async (name) => {
            const fn = nameModal.onConfirm;
            setNameModal(null);
            await fn(name);
          }}
          onCancel={() => setNameModal(null)}
        />
      )}

      <div>
        <h1 className="text-2xl font-bold text-neutral-900">Payroll</h1>
        <p className="text-sm text-neutral-600 mt-1">
          Set the pay period, choose or configure a report template (filters and columns), create a draft run, then
          finalize. Period settings are independent of saved templates.
        </p>
      </div>

      {/* Period filters — outside template */}
      <section className="bg-white border border-neutral-200 rounded-lg p-4 space-y-3">
        <h2 className="text-sm font-semibold text-neutral-900">Pay period</h2>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          {enabledCycles.length === 0 ? (
            <p className="text-xs text-amber-800">
              No payroll cycles enabled. Turn them on under Settings → Company Settings → Payroll Cycles.
            </p>
          ) : (
            enabledCycles.map(({ kind, label }) => (
              <label key={kind} className="inline-flex items-center gap-2 cursor-pointer text-sm text-neutral-800">
                <input
                  type="radio"
                  name="payrollBuilderFreq"
                  checked={freqKind === kind}
                  onChange={() => setFreqKind(kind)}
                  className="text-blue-600 border-neutral-300"
                />
                {label}
              </label>
            ))
          )}
        </div>
        <div className="max-w-md">
          {freqKind === 'ooh-hourly' ? (
            <div className="space-y-1">
              <label className="block text-xs font-medium text-neutral-700">
                <Calendar className="inline h-3.5 w-3.5 mr-1 text-neutral-500" aria-hidden />
                Hourly 29–28 cycle
              </label>
              {hourlyCyclePickerOpen ? (
                <WeekPickerCalendar
                  value={periodStartDate}
                  onChange={(ymd) => setPeriodStartDate(ymd)}
                  onClose={() => setHourlyCyclePickerOpen(false)}
                  weekStartsOn={weekStartsOn}
                  resolvePeriodStart={(date) => getOohHourlyCycleContaining(date).start}
                  isDayInSelection={(date, selectedStart) => {
                    const cycle = getOohHourlyCycleContaining(parseLocalDate(selectedStart));
                    const ymd = toLocalDateString(date);
                    return ymd >= cycle.start && ymd <= cycle.end;
                  }}
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setHourlyCyclePickerOpen(true)}
                  className="flex w-full items-center gap-2 px-3 py-2 border border-neutral-300 rounded-md bg-white hover:bg-neutral-50 text-left text-sm"
                >
                  <Calendar className="h-4 w-4 text-neutral-500 shrink-0" />
                  <span>{oohHourlyRangeLabel || 'Select 29–28 cycle'}</span>
                  <ChevronRight className="h-4 w-4 text-neutral-400 ml-auto" />
                </button>
              )}
            </div>
          ) : freqKind === 'monthly' ? (
            <div className="space-y-1">
              <label className="block text-xs font-medium text-neutral-700">
                <Calendar className="inline h-3.5 w-3.5 mr-1 text-neutral-500" aria-hidden />
                Salaried month
              </label>
              <input
                type="month"
                value={periodMonth}
                onChange={(e) => setPeriodMonth(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm bg-white"
              />
              {monthRangeLabel && (
                <p className="text-xs text-neutral-600">{monthRangeLabel}</p>
              )}
            </div>
          ) : freqKind === 'fortnightly' ? (
            <div className="space-y-1">
              <label className="block text-xs font-medium text-neutral-700">
                <Calendar className="inline h-3.5 w-3.5 mr-1 text-neutral-500" aria-hidden />
                Fortnight
              </label>
              {fortnightPickerOpen ? (
                <WeekPickerCalendar
                  value={periodStartDate}
                  onChange={(ymd) => setPeriodStartDate(ymd)}
                  onClose={() => setFortnightPickerOpen(false)}
                  weekStartsOn={weekStartsOn}
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setFortnightPickerOpen(true)}
                  className="flex w-full items-center gap-2 px-3 py-2 border border-neutral-300 rounded-md bg-white hover:bg-neutral-50 text-left text-sm"
                >
                  <Calendar className="h-4 w-4 text-neutral-500 shrink-0" />
                  <span>{fortnightRangeLabel || 'Select fortnight'}</span>
                  <ChevronRight className="h-4 w-4 text-neutral-400 ml-auto" />
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-1">
              <label className="block text-xs font-medium text-neutral-700">
                <Calendar className="inline h-3.5 w-3.5 mr-1 text-neutral-500" aria-hidden />
                Week
              </label>
              {weekPickerOpen ? (
                <WeekPickerCalendar
                  value={periodStartDate}
                  onChange={(ymd) => setPeriodStartDate(ymd)}
                  onClose={() => setWeekPickerOpen(false)}
                  weekStartsOn={weekStartsOn}
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setWeekPickerOpen(true)}
                  className="flex w-full items-center gap-2 px-3 py-2 border border-neutral-300 rounded-md bg-white hover:bg-neutral-50 text-left text-sm"
                >
                  <Calendar className="h-4 w-4 text-neutral-500 shrink-0" />
                  <span>{weekRangeLabel || 'Select week'}</span>
                  <ChevronRight className="h-4 w-4 text-neutral-400 ml-auto" />
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Template */}
      <div className="flex flex-col gap-2">
        <label className="text-xs font-semibold text-neutral-600 uppercase tracking-wide">Report template</label>
        <div className="flex flex-wrap items-center gap-1.5">
          <select
            value={selectedTemplateId}
            onChange={(e) => void handleTemplateSelectChange(e)}
            disabled={presetsLoading}
            className="min-w-[200px] flex-1 max-w-md px-3 py-2 text-sm border border-neutral-300 rounded-md bg-white"
          >
            <option value="">Select a saved template…</option>
            {templateList.map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.name}
                {p.isDefault ? ' (Default)' : ''}
              </option>
            ))}
          </select>
          <button
            type="button"
            title="New template from current settings"
            onClick={() => {
              clearTemplateSelection();
              openCreateTemplateModal('');
            }}
            className="inline-flex items-center justify-center p-2.5 border border-blue-200 bg-blue-50 rounded-md hover:bg-blue-100 text-blue-800"
          >
            <FilePlus className="h-5 w-5" />
          </button>
          <button
            type="button"
            title="Delete template"
            onClick={handleDeleteTemplate}
            className="inline-flex items-center justify-center p-2.5 border border-red-200 rounded-md hover:bg-red-50 text-red-700"
          >
            <Trash2 className="h-5 w-5" />
          </button>
          <button
            type="button"
            title={loadedTemplateId != null ? 'Save template' : 'Save new template'}
            onClick={handleSaveTemplate}
            disabled={templateSaving}
            className="inline-flex items-center justify-center p-2.5 rounded-md bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
          >
            {templateSaving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
          </button>
          {loadedTemplateId != null && (
            <button
              type="button"
              title="Save as new template"
              onClick={handleSaveAsNewTemplate}
              disabled={templateSaving}
              className="inline-flex items-center justify-center p-2.5 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50"
            >
              <Copy className="h-5 w-5" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="text"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="Template name"
            className="flex-1 min-w-[12rem] max-w-md px-3 py-2 text-sm border border-neutral-300 rounded-md"
          />
          <label className="inline-flex items-center gap-2 text-sm text-neutral-800 whitespace-nowrap">
            <input
              type="checkbox"
              checked={templateIsDefault}
              onChange={(e) => setTemplateIsDefault(e.target.checked)}
            />
            Is default
          </label>
        </div>
      </div>

      {/* Layout */}
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <span className="font-medium text-neutral-700">Layout:</span>
        <label className="inline-flex items-center gap-1.5 cursor-pointer">
          <input
            type="radio"
            checked={layout === 'detail'}
            onChange={() => {
              setLayout('detail');
              setOutputColumns(defaultOutputColumns('detail'));
            }}
          />
          Detail (line level)
        </label>
        <label className="inline-flex items-center gap-1.5 cursor-pointer">
          <input
            type="radio"
            checked={layout === 'summary'}
            onChange={() => {
              setLayout('summary');
              setOutputColumns(defaultOutputColumns('summary'));
            }}
          />
          Summary (per employee)
        </label>
      </div>

      {layout === 'detail' && (
        <section className="rounded-lg border border-neutral-200 bg-white p-4 space-y-3">
          <h2 className="text-sm font-semibold text-neutral-900">Report line breakdown</h2>
          <div className="inline-flex flex-wrap rounded-md border border-neutral-300 overflow-hidden">
            {(
              [
                { value: 'periodTotal' as PayrollLineBreakdown, label: 'Period total' },
                { value: 'scheduleBreakdown' as PayrollLineBreakdown, label: 'By schedule' },
                { value: 'scheduleDayTypeBreakdown' as PayrollLineBreakdown, label: 'Schedule + day type' },
              ] as const
            ).map(({ value, label }) => (
              <button
                key={value}
                type="button"
                onClick={() => {
                  setReportOptions((prev) => ({ ...prev, lineBreakdown: value }));
                  setPreviewRows(null);
                }}
                className={`px-3 py-1.5 text-sm font-medium ${
                  reportOptions.lineBreakdown === value
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-neutral-700 hover:bg-neutral-50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="text-xs text-neutral-500">
            Schedule + day type splits nurse banding rows (e.g. Nurse Supervisor — Saturday, Midweek red eye, Weekend red eye).
          </p>
          <div className="flex flex-wrap gap-4 text-sm text-neutral-800">
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={reportOptions.groupByEmployee}
                onChange={(e) =>
                  setReportOptions((prev) => ({ ...prev, groupByEmployee: e.target.checked }))
                }
              />
              Group by employee
            </label>
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={reportOptions.onlyWarnings}
                onChange={(e) =>
                  setReportOptions((prev) => ({ ...prev, onlyWarnings: e.target.checked }))
                }
              />
              Warnings only
            </label>
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={reportOptions.onlyErrors}
                onChange={(e) =>
                  setReportOptions((prev) => ({ ...prev, onlyErrors: e.target.checked }))
                }
              />
              Errors only
            </label>
          </div>
        </section>
      )}

      {/* Filters + columns */}
      <div className="flex flex-col md:flex-row gap-6 md:items-stretch">
        <div className="w-full md:flex-1 flex flex-col border border-neutral-200 rounded-lg bg-white overflow-hidden">
          <div className="flex items-stretch border-b border-neutral-200 bg-neutral-50">
            <button
              type="button"
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              className="flex-1 flex items-center gap-2 px-4 py-3 text-left hover:bg-neutral-100"
            >
              {filtersExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
              <span className="font-semibold text-neutral-900">Filters</span>
            </button>
            <button
              type="button"
              title="Edit filters"
              onClick={openFilterEditModal}
              className="px-3 border-l border-neutral-200 hover:bg-neutral-100"
            >
              <Pencil className="h-5 w-5 text-neutral-700" />
            </button>
          </div>
          {filtersExpanded && (
            <div className="p-4">
              <EmployeeAdvancedFilterPanel
                filters={filters}
                setFilters={setFilters}
                filterFields={filterFields}
                relatedDataLoading={false}
                readOnly
                excludeFieldValues={['employeeReportStatus', 'mainLocationId']}
                nonDeletableFieldValues={['employeeReportStatus', 'mainLocationId']}
                disableFieldSelectForFieldValues={['employeeReportStatus', 'mainLocationId']}
                onClearFiltersOverride={() => setFilters(defaultMandatoryFilters())}
              />
            </div>
          )}
        </div>

        <div className="w-full md:flex-1 flex flex-col border border-neutral-200 rounded-lg bg-white overflow-hidden">
          <div className="flex items-stretch border-b border-neutral-200 bg-neutral-50">
            <button
              type="button"
              onClick={() => setColumnsExpanded(!columnsExpanded)}
              className="flex-1 flex items-center gap-2 px-4 py-3 text-left hover:bg-neutral-100"
            >
              {columnsExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
              <span className="font-semibold text-neutral-900">Output columns</span>
            </button>
            <button
              type="button"
              title="Edit columns"
              onClick={openColumnModal}
              className="px-3 border-l border-neutral-200 hover:bg-neutral-100"
            >
              <Pencil className="h-5 w-5 text-neutral-700" />
            </button>
          </div>
          {columnsExpanded && (
            <div className="p-4 flex flex-wrap gap-2">
              {outputColumns.map((col) => (
                <span
                  key={col.id}
                  className="inline-flex rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-1.5 text-sm font-medium text-neutral-800"
                >
                  {col.headerLabel.trim() ||
                    PAYROLL_REPORT_OUTPUT_FIELD_CATALOG.find((c) => c.key === col.fieldKey)?.defaultLabel ||
                    col.fieldKey}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <section className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={handleCreateDraft}
          disabled={isCreateJobRunning || !effectivePeriodStart || enabledCycles.length === 0}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-neutral-900 text-white text-sm font-medium rounded-md hover:bg-neutral-800 disabled:opacity-50"
        >
          {isCreateJobRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <DollarSign className="h-4 w-4" />}
          Create draft
        </button>
        {isRunReadyForView(activeRun?.status) && preview && (
          <>
            {canForce && preview.hasHourlyMismatchBlocker && !preview.hasCalculationErrors && (
              <label className="inline-flex items-center gap-2 text-sm text-neutral-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={forceFinalize}
                  onChange={(ev) => setForceFinalize(ev.target.checked)}
                />
                Force finalize
              </label>
            )}
            <button
              type="button"
              onClick={handleFinalize}
              disabled={
                isFinalizeJobRunning ||
                (!preview.canFinalize &&
                  !(forceFinalize && canForce && preview.hasHourlyMismatchBlocker && !preview.hasCalculationErrors))
              }
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-700 text-white text-sm font-medium rounded-md hover:bg-emerald-800 disabled:opacity-50"
            >
              {isFinalizeJobRunning ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle2 className="h-4 w-4" />
              )}
              Finalize run
            </button>
          </>
        )}
        <button
          type="button"
          onClick={handlePreviewReport}
          disabled={!previewRows?.length}
          className="inline-flex items-center gap-2 px-4 py-2.5 border border-neutral-300 text-sm font-medium rounded-md hover:bg-neutral-50 disabled:opacity-50"
        >
          <Eye className="h-4 w-4" />
          Preview report
        </button>
      </section>

      {statusMessage && (
        <div className="text-sm text-neutral-700 bg-neutral-100 border border-neutral-200 rounded-md px-3 py-2">
          {statusMessage}
        </div>
      )}
      {previewError && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{previewError}</div>
      )}

      {activeRunId != null && (
        <section className="space-y-2 border border-neutral-200 rounded-lg p-4 bg-white">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-neutral-900">
              Current run #{activeRunId}
              {activeRun && (
                <span className="font-normal text-neutral-600 ml-2">
                  {formatYmd(activeRun.periodStart)} – {formatYmd(activeRun.periodEnd)} · {activeRun.status}
                </span>
              )}
            </h3>
            {previewLoading && <Loader2 className="h-4 w-4 animate-spin text-neutral-500" />}
            {employeesLoading && !previewLoading && (
              <span className="text-xs text-neutral-500">Loading employee data…</span>
            )}
          </div>
          {preview &&
            isRunReadyForView(activeRun?.status) &&
            !preview.canFinalize &&
            preview.finalizeBlockers.length > 0 && (
            <div className="flex items-start gap-2 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <ul className="list-disc list-inside">
                {preview.finalizeBlockers.slice(0, 5).map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
            </div>
          )}
          {isRunProcessing(activeRun?.status) ? (
            <p className="text-sm text-neutral-600">
              Record processing is running in the background. This page will refresh status automatically — you can
              leave and come back when it shows Ready for View.
            </p>
          ) : previewRows && previewRows.length > 0 ? (
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-sm text-neutral-600">
                {previewRows.length} row{previewRows.length === 1 ? '' : 's'} match current filters and columns.
              </p>
              <button
                type="button"
                onClick={() => activeRun && void openRunReportView(activeRun)}
                className="inline-flex items-center gap-1 text-sm font-medium text-neutral-800 hover:text-neutral-900"
              >
                <Eye className="h-4 w-4" />
                View rows
              </button>
            </div>
          ) : preview && !previewLoading ? (
            <p className="text-sm text-neutral-500">No rows match the current filters.</p>
          ) : isRunReadyForView(activeRun?.status) && !previewLoading ? (
            <p className="text-sm text-neutral-600">
              Records are ready. Use the View action on the run row to open them (or re-run processing).
            </p>
          ) : null}
        </section>
      )}

      {/* Run history */}
      <section className="bg-white border border-neutral-200 rounded-lg p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-neutral-900">Run history</h2>
          <div className="flex flex-wrap items-center gap-2">
            {runs.length > 0 && (
              <button
                type="button"
                onClick={() => void handleClearAllHistory()}
                disabled={clearingHistory || loadingRuns}
                className="inline-flex items-center gap-1 text-xs font-medium text-red-700 hover:text-red-800 disabled:opacity-50"
              >
                {clearingHistory ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Trash2 className="w-3.5 h-3.5" />
                )}
                Clear all drafts
              </button>
            )}
            <button
              type="button"
              onClick={() => void loadRuns({ silent: runs.length > 0 })}
              className="inline-flex items-center gap-1 text-xs text-neutral-600 hover:text-neutral-900"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loadingRuns || refreshingRuns ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <input
            type="search"
            value={historySearch}
            onChange={(e) => setHistorySearch(e.target.value)}
            placeholder="Search by ID, period, hourly/salaried, status, notes…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-neutral-300 rounded-md"
          />
        </div>
        {loadingRuns && runs.length === 0 ? (
          <div className="flex items-center gap-2 text-neutral-500 text-sm py-4">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : filteredRuns.length === 0 && activeRunJobs.length === 0 ? (
          <p className="text-sm text-neutral-500 py-4 text-center border border-dashed border-neutral-200 rounded-lg">
            {runs.length === 0 ? 'No payroll runs yet.' : 'No runs match your search.'}
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-neutral-500 border-b border-neutral-200">
                    <th className="pb-2 pr-3">ID</th>
                    <th className="pb-2 pr-3">Pay period</th>
                    <th className="pb-2 pr-3">Status</th>
                    <th className="pb-2 pr-3">Notes</th>
                    <th className="pb-2 w-48 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {activeRunJobs
                    .filter((j) => j.kind === 'create' && j.runId == null)
                    .map((job) => (
                      <tr key={job.id} className="border-b border-neutral-100 bg-neutral-50/80">
                        <td className="py-2 pr-3 font-mono text-neutral-400">—</td>
                        <td className="py-2 pr-3">
                          {job.periodStart && job.periodEnd
                            ? formatPayrollRunPeriodRow({
                                id: 0,
                                periodStart: job.periodStart,
                                periodEnd: job.periodEnd,
                                status: 'Draft',
                                createdAt: job.startedAt.toISOString(),
                                previewFiltersJson: JSON.stringify({
                                  freqKind: job.freqKind ?? 'weekly',
                                  payCategoryFilter:
                                    job.freqKind === 'ooh-hourly'
                                      ? 'hourly'
                                      : job.freqKind === 'monthly'
                                        ? 'salaried'
                                        : 'both',
                                  payrollDepartmentId: null,
                                  payrollDepartmentName: null,
                                  mainLocationId: null,
                                  mainLocationName: null,
                                  paymentFrequencyId: null,
                                  paymentFrequencyName: null,
                                }),
                              })
                            : '—'}
                        </td>
                        <td className="py-2 pr-3">
                          <ReportRunStatusBadge status={job.status} />
                        </td>
                        <td className="py-2 pr-3 text-neutral-600 truncate max-w-xs">
                          {job.status === 'failed' ? job.error || 'Create failed' : 'Creating draft…'}
                        </td>
                        <td className="py-2 text-right" />
                      </tr>
                    ))}
                  {paginatedRuns.map((r) => {
                    const rowStatus = getRunRowStatus(r);
                    const isClientPending = rowStatus === 'pending';
                    const isServerProcessing = rowStatus === 'processing';
                    return (
                      <tr
                        key={r.id}
                        className={`border-b border-neutral-100 cursor-pointer hover:bg-neutral-50 ${
                          activeRunId === r.id ? 'bg-blue-50/60' : ''
                        } ${isClientPending || isServerProcessing ? 'opacity-80' : ''}`}
                        onClick={() => !isClientPending && handleRunRowClick(r)}
                      >
                        <td className="py-2 pr-3 font-mono">{r.id}</td>
                        <td className="py-2 pr-3">{formatPayrollRunPeriodRow(r)}</td>
                        <td className="py-2 pr-3">
                          <ReportRunStatusBadge status={rowStatus} />
                        </td>
                        <td className="py-2 pr-3 text-neutral-600 truncate max-w-xs">
                          {rowStatus === 'failed'
                            ? runJobs.find((j) => j.runId === r.id && j.status === 'failed')?.error ||
                              r.previewProcessingError ||
                              r.notes ||
                              '—'
                            : isServerProcessing
                              ? 'Processing in background…'
                              : r.notes || '—'}
                        </td>
                        <td className="py-2 text-right">
                          <div className="flex flex-wrap items-center justify-end gap-1">
                            <button
                              type="button"
                              title="View report rows"
                              disabled={isClientPending || historyAction?.runId === r.id}
                              onClick={(e) => void openRunReportView(r, e)}
                              className="inline-flex items-center justify-center rounded border border-neutral-200 p-1.5 text-neutral-800 hover:bg-neutral-100 disabled:opacity-40"
                            >
                              {historyAction?.runId === r.id && historyAction.kind === 'view' ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Eye className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              type="button"
                              title="Audit calculations"
                              disabled={isClientPending || isServerProcessing || isRunFailed(r.status)}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/reports/payroll-runs/${r.id}/audit`);
                              }}
                              className="inline-flex items-center justify-center rounded border border-indigo-200 p-1.5 text-indigo-700 hover:bg-indigo-50 disabled:opacity-40"
                            >
                              <ClipboardList className="w-4 h-4" />
                            </button>
                            {(['excel', 'pdf'] as const).map((kind) => (
                              <button
                                key={kind}
                                type="button"
                                title={kind === 'excel' ? 'Download Excel' : 'Download PDF'}
                                disabled={isClientPending || historyAction?.runId === r.id}
                                onClick={(e) => void exportRunReport(r, kind, e)}
                                className={`inline-flex items-center justify-center rounded border p-1.5 disabled:opacity-40 ${
                                  kind === 'excel'
                                    ? 'border-green-200 text-green-700 hover:bg-green-50'
                                    : 'border-rose-200 text-rose-700 hover:bg-rose-50'
                                }`}
                              >
                                {historyAction?.runId === r.id && historyAction.kind === kind ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : kind === 'excel' ? (
                                  <FileSpreadsheet className="w-4 h-4" />
                                ) : (
                                  <FileDown className="w-4 h-4" />
                                )}
                              </button>
                            ))}
                            <button
                              type="button"
                              title="Download CSV"
                              disabled={isClientPending || historyAction?.runId === r.id}
                              onClick={(e) => void exportRunReport(r, 'csv', e)}
                              className="inline-flex items-center justify-center rounded border border-blue-200 p-1.5 text-blue-700 hover:bg-blue-50 disabled:opacity-40"
                            >
                              {historyAction?.runId === r.id && historyAction.kind === 'csv' ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Download className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              type="button"
                              title="Delete run"
                              disabled={
                                isRunFinalized(r.status) || deletingId === r.id || isClientPending
                              }
                              onClick={(e) => void handleDeleteRun(e, r)}
                              className="inline-flex items-center justify-center rounded border border-red-200 p-1.5 text-red-700 hover:bg-red-50 disabled:opacity-40"
                            >
                              {deletingId === r.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 pt-2 text-sm text-neutral-600">
              <span>
                Showing {(historyPage - 1) * RUN_HISTORY_PAGE_SIZE + 1}–
                {Math.min(historyPage * RUN_HISTORY_PAGE_SIZE, filteredRuns.length)} of {filteredRuns.length}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={historyPage <= 1}
                  onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                  className="inline-flex items-center gap-1 px-2 py-1 border border-neutral-300 rounded hover:bg-neutral-50 disabled:opacity-40"
                >
                  <ChevronLeft className="h-4 w-4" /> Prev
                </button>
                {Array.from({ length: historyTotalPages }, (_, i) => i + 1)
                  .filter((p) => {
                    if (historyTotalPages <= 7) return true;
                    return Math.abs(p - historyPage) <= 2 || p === 1 || p === historyTotalPages;
                  })
                  .map((p, idx, arr) => {
                    const prev = arr[idx - 1];
                    const showEllipsis = prev != null && p - prev > 1;
                    return (
                      <React.Fragment key={p}>
                        {showEllipsis && <span className="px-1 text-neutral-400">…</span>}
                        <button
                          type="button"
                          onClick={() => setHistoryPage(p)}
                          className={`min-w-[2rem] px-2 py-1 border rounded text-sm ${
                            p === historyPage
                              ? 'bg-neutral-900 text-white border-neutral-900'
                              : 'border-neutral-300 hover:bg-neutral-50'
                          }`}
                        >
                          {p}
                        </button>
                      </React.Fragment>
                    );
                  })}
                <button
                  type="button"
                  disabled={historyPage >= historyTotalPages}
                  onClick={() => setHistoryPage((p) => Math.min(historyTotalPages, p + 1))}
                  className="inline-flex items-center gap-1 px-2 py-1 border border-neutral-300 rounded hover:bg-neutral-50 disabled:opacity-40"
                >
                  Next <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </section>

      {/* Filter edit modal */}
      {filterEditModalOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40"
          role="dialog"
          aria-modal="true"
          onClick={() => !filterModalSaving && setFilterEditModalOpen(false)}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col border border-neutral-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200">
              <h2 className="text-base font-semibold">Edit report filters</h2>
              <button type="button" onClick={() => setFilterEditModalOpen(false)} className="p-1.5 hover:bg-neutral-100 rounded">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <EmployeeAdvancedFilterPanel
                filters={filterModalDraft}
                setFilters={setFilterModalDraft}
                filterFields={filterFields}
                relatedDataLoading={false}
                excludeFieldValues={['employeeReportStatus', 'mainLocationId']}
                nonDeletableFieldValues={['employeeReportStatus', 'mainLocationId']}
                disableFieldSelectForFieldValues={['employeeReportStatus', 'mainLocationId']}
                onClearFiltersOverride={() => setFilterModalDraft(defaultMandatoryFilters())}
              />
            </div>
            <div className="flex justify-end gap-2 px-4 py-3 border-t border-neutral-200">
              <button
                type="button"
                onClick={() => setFilterEditModalOpen(false)}
                className="px-4 py-2 text-sm border border-neutral-300 rounded-md hover:bg-neutral-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={filterModalSaving}
                onClick={() => void handleSaveFilterModal()}
                className="px-4 py-2 text-sm font-medium text-white bg-neutral-900 rounded-md hover:bg-neutral-800 disabled:opacity-50"
              >
                {filterModalSaving ? 'Saving…' : 'Apply'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Column edit modal */}
      {columnLabelsModalOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40"
          role="dialog"
          aria-modal="true"
          onClick={() => !columnModalSaving && setColumnLabelsModalOpen(false)}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col border border-neutral-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200">
              <div>
                <h2 className="text-base font-semibold">Output columns</h2>
                {loadedTemplateId != null && (
                  <input
                    type="text"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="Template name"
                    className="mt-1 w-full max-w-xs text-sm border border-neutral-300 rounded px-2 py-1"
                  />
                )}
              </div>
              <button type="button" onClick={() => setColumnLabelsModalOpen(false)} className="p-1.5 hover:bg-neutral-100 rounded">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              <div className="flex flex-wrap gap-2">
                <select
                  value={columnModalAddFieldKey}
                  onChange={(e) => setColumnModalAddFieldKey(e.target.value)}
                  className="flex-1 min-w-[140px] text-sm border border-neutral-300 rounded-md px-2 py-1.5"
                >
                  <option value="">Add field…</option>
                  {addableColumnFields.map((f) => (
                    <option key={f.key} value={f.key}>
                      {f.defaultLabel}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  disabled={!columnModalAddFieldKey}
                  onClick={() => {
                    if (!columnModalAddFieldKey) return;
                    setColumnModalDraft((prev) => [
                      ...prev,
                      {
                        id: newRowId(),
                        fieldKey: columnModalAddFieldKey,
                        headerLabel: getDefaultLabelForPayrollOutputField(columnModalAddFieldKey),
                      },
                    ]);
                    setColumnModalAddFieldKey('');
                  }}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  Add
                </button>
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {columnModalDraft.map((col, index) => (
                    <tr key={col.id} className="border-b border-neutral-100">
                      <td className="py-2 pr-2 w-8">
                        <GripVertical className="h-4 w-4 text-neutral-400" />
                      </td>
                      <td className="py-2 pr-2">
                        <input
                          type="text"
                          value={col.headerLabel}
                          onChange={(e) =>
                            setColumnModalDraft((prev) =>
                              prev.map((c) => (c.id === col.id ? { ...c, headerLabel: e.target.value } : c))
                            )
                          }
                          className="w-full border border-neutral-300 rounded px-2 py-1"
                        />
                      </td>
                      <td className="py-2 pr-2 text-neutral-500 text-xs">
                        {getDefaultLabelForPayrollOutputField(col.fieldKey)}
                      </td>
                      <td className="py-2 flex gap-1">
                        <button
                          type="button"
                          disabled={index === 0}
                          onClick={() =>
                            setColumnModalDraft((prev) => {
                              const next = [...prev];
                              [next[index - 1], next[index]] = [next[index], next[index - 1]];
                              return next;
                            })
                          }
                          className="px-2 py-0.5 text-xs border rounded disabled:opacity-40"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          disabled={index === columnModalDraft.length - 1}
                          onClick={() =>
                            setColumnModalDraft((prev) => {
                              const next = [...prev];
                              [next[index], next[index + 1]] = [next[index + 1], next[index]];
                              return next;
                            })
                          }
                          className="px-2 py-0.5 text-xs border rounded disabled:opacity-40"
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          onClick={() => setColumnModalDraft((prev) => prev.filter((c) => c.id !== col.id))}
                          className="px-2 py-0.5 text-xs border border-red-200 text-red-700 rounded"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-2 px-4 py-3 border-t border-neutral-200">
              <button
                type="button"
                onClick={() => setColumnLabelsModalOpen(false)}
                className="px-4 py-2 text-sm border border-neutral-300 rounded-md hover:bg-neutral-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={columnModalSaving}
                onClick={() => void handleSaveColumnModal()}
                className="px-4 py-2 text-sm font-medium text-white bg-neutral-900 rounded-md hover:bg-neutral-800 disabled:opacity-50"
              >
                {columnModalSaving ? 'Saving…' : 'Apply'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview modal */}
      {previewModalOpen && previewRows && previewModalRun && preview && (
        <div
          className="fixed inset-0 z-[102] flex items-center justify-center p-4 bg-black/40"
          role="dialog"
          aria-modal="true"
          onClick={() => setPreviewModalOpen(false)}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-6xl max-h-[92vh] flex flex-col border border-neutral-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-2 px-4 py-3 border-b border-neutral-200 bg-neutral-50">
              <div>
                <h2 className="text-base font-semibold text-neutral-900">Payroll report — run #{previewModalRun.id}</h2>
                <p className="text-xs text-neutral-600 mt-1">
                  {formatYmd(previewModalRun.periodStart)} → {formatYmd(previewModalRun.periodEnd)} ·{' '}
                  {previewModalRun.status}
                  {previewModalRun.notes ? ` · ${previewModalRun.notes}` : ''} · {previewRows.length} row
                  {previewRows.length === 1 ? '' : 's'}
                </p>
              </div>
              <button type="button" onClick={() => setPreviewModalOpen(false)} className="p-1.5 hover:bg-neutral-100 rounded">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 sticky top-0">
                  <tr>
                    {outputColumns.map((col) => (
                      <th key={col.id} className="px-3 py-2 text-left font-medium whitespace-nowrap">
                        {col.headerLabel.trim() || getDefaultLabelForPayrollOutputField(col.fieldKey)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const runPayPeriodType = resolvePayPeriodType(previewModalRun);
                    const runPayPeriodLabel = resolvePayPeriodLabel(previewModalRun);
                    let previousEmployeeId: number | null = null;
                    return previewRows.map((row, ri) => {
                      const cells = outputColumns.map((col) =>
                        String(
                          formatPayrollReportCell(row, col.fieldKey, {
                            preview,
                            runStatus: previewModalRun.status ?? null,
                            worksNumberByEmployeeId,
                            firstNameByEmployeeId,
                            lastNameByEmployeeId,
                            payPeriodType: runPayPeriodType,
                            payPeriodLabel: runPayPeriodLabel,
                            groupByEmployee: false,
                            previousEmployeeId,
                          }) ?? ''
                        )
                      );
                      previousEmployeeId = row.employee.employeeId;
                      return (
                        <tr key={ri} className="border-b border-neutral-100">
                          {cells.map((cell, ci) => (
                            <td key={ci} className="px-3 py-2 whitespace-nowrap max-w-xs truncate">
                              {cell}
                            </td>
                          ))}
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap justify-end gap-2 px-4 py-3 border-t border-neutral-200">
              {(['csv', 'excel', 'pdf'] as const).map((kind) => (
                <button
                  key={kind}
                  type="button"
                  onClick={() => void exportRunReport(previewModalRun, kind)}
                  className={`inline-flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md border ${
                    kind === 'csv'
                      ? 'text-blue-700 bg-blue-50 border-blue-200 hover:bg-blue-100'
                      : kind === 'excel'
                        ? 'text-green-700 bg-green-50 border-green-200 hover:bg-green-100'
                        : 'text-rose-700 bg-rose-50 border-rose-200 hover:bg-rose-100'
                  }`}
                >
                  {kind === 'csv' ? (
                    <Download className="h-4 w-4" />
                  ) : kind === 'excel' ? (
                    <FileSpreadsheet className="h-4 w-4" />
                  ) : (
                    <FileDown className="h-4 w-4" />
                  )}
                  {kind === 'excel' ? 'Excel' : kind.toUpperCase()}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setPreviewModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-white bg-neutral-800 rounded-md hover:bg-neutral-900"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollBuilderPage;
